import { getCurrentEnvironment } from '../constants/environments'

/** Adoption threshold (%) above which a feature flag is considered "GA" */
export const GA_ADOPTION_THRESHOLD = 100

/**
 * Determine if a feature flag qualifies as "Generally Available".
 * GA = default_value is true AND adoption percentage >= threshold.
 */
export const isGA = (
  defaultValue: boolean | null | undefined,
  adoptionPercentage: number | null | undefined
): boolean => {
  return (
    defaultValue === true && (adoptionPercentage ?? 0) >= GA_ADOPTION_THRESHOLD
  )
}

interface FeatureFlagAdoption {
  feature_flag_on?: number
  feature_flag_off?: number
  default_value?: boolean | null
  total_organizations?: number // paid-org denominator
}

/**
 * Adoption rate (%) of a feature flag across paid orgs, or null when unknown.
 */
export const getAdoptionPercentage = (
  flag: FeatureFlagAdoption
): number | null => {
  const {
    feature_flag_on,
    feature_flag_off,
    default_value,
    total_organizations
  } = flag
  if (total_organizations == null || total_organizations === 0) {
    return null
  }

  const enabled = default_value
    ? total_organizations - (feature_flag_off ?? 0)
    : (feature_flag_on ?? 0)

  if (enabled >= total_organizations) {
    return 100
  }
  // Capped at 99: rounding alone made a single paid opt-out (581/582) read as 100%,
  // indistinguishable from a flag no paid org has turned off.
  return Math.min(99, Math.round((enabled / total_organizations) * 100))
}

/**
 * Number of orgs with a feature flag effectively enabled, or null when unknown.
 * When the default is on, orgs without an explicit override count as enabled.
 */
export const getEnabledOrgCount = (
  flag: FeatureFlagAdoption
): number | null => {
  const {
    feature_flag_on,
    feature_flag_off,
    default_value,
    total_organizations
  } = flag
  if (total_organizations == null) {
    return null
  }
  return default_value
    ? total_organizations - (feature_flag_off ?? 0)
    : (feature_flag_on ?? 0)
}

const GITSTREAM_ENVIRONMENTS = ['dev-01', 'prod']

/**
 * Check if GitStream features are available in the current environment.
 * GitStream backends exist only for prod and dev-01.
 */
export const isGitStreamAvailable = (): boolean => {
  const currentEnv = getCurrentEnvironment()
  return GITSTREAM_ENVIRONMENTS.includes(currentEnv.id)
}

/**
 * Get a message explaining why GitStream is not available
 */
export const getGitStreamUnavailableMessage = (): string => {
  const currentEnv = getCurrentEnvironment()
  return `gitStream features are only available in ${GITSTREAM_ENVIRONMENTS.join(' and ')}. You are currently connected to ${currentEnv.id}.`
}
